/*
 *   File : selectOption.js
 *   Author : https://evoqins.com
 *   Description : selectOption component
 *   Integrations : NA
 *   Version : 1.0.0
 */

import React, { useState, useEffect } from "react";
import Select from "react-select";
import Icon from "../icons";
import { createPortal } from "react-dom";
import { formatInputErrorMessage } from "../../Utils/format-input-error";

/* ------------------------------------------
   INTERNAL OVERLAY COMPONENT (with portal)
------------------------------------------- */
const BodyOverlay = ({ onClose, zIndex = 1040 }) => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "auto";

    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="position-fixed top-0 start-0 w-100 h-100"
      style={{
        background: "rgba(0, 0, 0, 0.3)",
        zIndex,
      }}
      onClick={onClose}
    />,
    document.body
  );
};

/* ------------------------------------------
   MAIN COMPONENT
------------------------------------------- */
export default function CheckboxSelect({
  options = [],
  value,
  error = "",
  onChange,
  placeholder = "Select",
  isMulti = false,
}) {
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const DropdownArrow = () => (
    <Icon icon="chevron-down" size={17} className="me-2" />
  );

  const components = {
    DropdownIndicator: DropdownArrow,
  };

  const portalTarget = typeof document !== "undefined" ? document.body : null;

  return (
    <div
      className={`custom-filter-single-select-wrapper position-relative ${
        error ? "custom-filter-single-select-wrapper__error" : ""
      }`}
    >
      {/* Overlay only when menu is open */}
      {menuIsOpen && <BodyOverlay onClose={() => setMenuIsOpen(false)} />}

      <Select
        options={options}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        isMulti={isMulti}
        isSearchable={false}
        className="custom-filter-single-select"
        classNamePrefix="custom-filter"
        closeMenuOnSelect={!isMulti}
        menuPlacement="auto"
        menuPortalTarget={portalTarget}
        menuPosition="fixed"
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
        components={components}
        styles={{
          control: (base) => ({
            ...base,
            borderColor: error ? "#C10818" : base.borderColor,
            borderWidth: error ? "1px" : base.borderWidth,
            borderStyle: error ? "solid" : base.borderStyle,
            boxShadow: "none",
            "&:hover": {
              ...base[":hover"],
              borderColor: error ? "#C10818" : base[":hover"]?.borderColor,
            },
          }),
          menuPortal: (base) => ({ ...base, zIndex: 1055 }),
          menu: (base) => ({
            ...base,
            maxHeight: 250,
            overflow: "hidden",
            borderRadius: 20,
          }),
          menuList: (base) => ({
            ...base,
            maxHeight: 250,
            overflowY: "auto",
            overflowX: "hidden",
          }),
        }}
      />

      {error ? (
        <p className="mb-0 fs-12 text-cg-red pt-1">
          {formatInputErrorMessage(error)}
        </p>
      ) : null}
    </div>
  );
}
